import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { getPasswordResetContinueUrl } from '../../utils/passwordResetUrl';

interface ForgotPasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

const ForgotPasswordDialog: React.FC<ForgotPasswordDialogProps> = ({ open, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    try {
      // Send password reset email
      // The email will contain a link that opens in the browser
      await sendPasswordResetEmail(auth, email, {
        url: getPasswordResetContinueUrl(),
        handleCodeInApp: false,
      });
      
      setSuccess(true);
    } catch (error: any) {
      console.error('Password reset error:', error);
      
      // Handle specific error cases
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email address.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many requests. Please try again later.');
      } else {
        setError(error.message || 'Failed to send password reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setEmail('');
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <EmailIcon color="primary" />
          <Typography variant="h6">Reset Password</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {success ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                Password reset email sent!
              </Typography>
            </Alert>
            <Typography variant="body2" paragraph>
              We've sent a password reset link to <strong>{email}</strong>
            </Typography>
            <Typography variant="body2" paragraph>
              Please check your email and click the link to reset your password. The link will expire in 1 hour.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Note:</strong> If you don't see the email, check your spam folder.
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" paragraph>
              Enter your email address and we'll send you a link to reset your password.
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                disabled={loading}
                autoFocus
                placeholder="your.email@example.com"
                helperText="We'll send a password reset link to this email"
              />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        {success ? (
          <Button
            onClick={handleClose}
            variant="contained"
            fullWidth
          >
            Close
          </Button>
        ) : (
          <>
            <Button
              onClick={handleClose}
              disabled={loading}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading || !email}
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <EmailIcon />}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ForgotPasswordDialog;
